import FormField from "components/FormField";
import ContactListItem from "../ContactSelector/ContactListItem";
import SelectedContactItem from "../ContactSelector/SelectedContactItem";
import SortArrow from "../SortArrow/SortArrow";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import styles from "./ContactsTable.module.scss";

const ContactsTable = ({
  title,
  searchQuery,
  onSearchChange,
  showSearch = true,
  contacts,
  selectedContactIds,
  onContactToggle,
  onContactRemove,
  contactsLoading,
  contactsError,
  hasMore,
  onLoadMore,
  headerActions,
  emptyStateMessage = "No contacts found",
  isSelectedContactsMode = false,
  className,
  tableClassName,
  searchClassName,
  sortDirection = null,
  sortPeriod = "7d",
  onSortClick,
  onPeriodChange,
}) => {
  const { sentinelRef, rootRef, triggerLoadMore } = useInfiniteScroll({
    onLoadMore,
    enabled: Boolean(hasMore) && !contactsLoading,
  });

  return (
    <div className={`${styles.panel} ${className || ""}`}>
      <div className={styles.panelHeader}>
        <h4>{title}</h4>
        {headerActions}
      </div>

      <div className={`${styles.contactsTable} ${tableClassName || ""}`}>
        <div className={styles.tableHeader}>
          <div className={styles.headerCell}></div>
          <div className={styles.headerCell}>Contact / Customer</div>
          <div className={styles.headerCell}>
            {onSortClick ? (
              <div className={styles.sortableHeader}>
                <span
                  className={`${styles.sortableLabel} ${sortDirection ? styles.sortActive : ""}`}
                  onClick={onSortClick}
                >
                  Activity
                  <SortArrow direction={sortDirection} />
                </span>
                {onPeriodChange && (
                  <span className={styles.periodToggle}>
                    (
                    <span
                      className={`${styles.periodOption} ${sortPeriod === "7d" ? styles.periodActive : ""}`}
                      onClick={() => onPeriodChange("7d")}
                    >
                      7d
                    </span>
                    {" | "}
                    <span
                      className={`${styles.periodOption} ${sortPeriod === "30d" ? styles.periodActive : ""}`}
                      onClick={() => onPeriodChange("30d")}
                    >
                      30d
                    </span>
                    )
                  </span>
                )}
              </div>
            ) : (
              "Activity (7d | 30d)"
            )}
          </div>
        </div>

        {showSearch && (
          <div className={styles.searchRow}>
            <FormField
              fieldName="contactSearch"
              placeholderText="Search contacts..."
              value={searchQuery}
              onChange={onSearchChange}
              className={`${styles.searchInput} ${searchClassName || ""}`}
            />
          </div>
        )}

        <div ref={rootRef} className={styles.tableBody}>
          {contactsError && (
            <div className={styles.error}>{contactsError}</div>
          )}

          {contacts.length === 0 && !contactsLoading && (
            <div className={styles.emptyState}>
              {isSelectedContactsMode ? (
                <>
                  <div className={styles.emptyIcon}>📋</div>
                  <div>{emptyStateMessage}</div>
                  <div style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
                    Select contacts from the left panel
                  </div>
                </>
              ) : searchQuery ? (
                "No contacts found matching your search."
              ) : (
                emptyStateMessage
              )}
            </div>
          )}

          {contacts.map((contact) => (
            <ContactListItem
              key={contact.id}
              contact={contact}
              isSelected={
                selectedContactIds ? selectedContactIds.has(contact.id) : false
              }
              onToggle={
                isSelectedContactsMode
                  ? () => onContactRemove(contact.id)
                  : onContactToggle
              }
              isRemoveMode={isSelectedContactsMode}
            />
          ))}

          {contactsLoading && (
            <div className={styles.loading}>Loading contacts...</div>
          )}

          {hasMore && !contactsLoading && contacts.length > 0 && (
            <div
              ref={sentinelRef}
              className={styles.loadMoreContainer}
              onClick={triggerLoadMore}
            >
              <div className={styles.loadMoreIcon}>↻</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactsTable;
